/* ------------ storyPreview is the button component for stories ------------ */

import { useTriggerEventContext } from '@components/utils/contexts/TriggerEventContext';
import InternalLink from '@components/utils/links/internalLink';
import PlayButton from '@components/utils/playButton';
import { StyledProps } from '@emotion/react';
import styled from '@emotion/styled';
import {
    Enum_Modals_Trigger,
    ContentStoryFragment,
} from '@graphql/generated/graphql';
import StrapiImage from '@lib/image/strapiImage';
import { getStrapiAssetUrl } from '@lib/strapi';
import { useRouter } from 'next/dist/client/router';
import { MouseEventHandler } from 'react';

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */

export type PropsType = {
    story: NonNullable<ContentStoryFragment['Story']>;
    context: {
        module: string;
        story_order: number;
        fromPage: string;
        searchQuery?: string;
    };
    id: number;
    storyLength: number;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
};

type BackgroundPropsType = {
    url: string;
};

/* -------------------------------------------------------------------------- */
/*                                   STYLED                                   */
/* -------------------------------------------------------------------------- */

const Background = styled.div<StyledProps<BackgroundPropsType>>`
    border-radius: ${(props) => props.theme.spacing.md};

    height: clamp(14rem, 75vw, 20rem); /*120px;*/
    width: clamp(12rem, 65vw, 17rem); /*100px;*/
    padding: ${(props) => props.theme.spacing.xs};
    flex-shrink: 0;

    overflow: hidden;
    position: relative;

    back &:active {
        transform: translateY(-1px);
    }
`;

const CardBody = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    height: 100%;

    & > button {
        margin-bottom: ${(props) => props.theme.spacing.xl};
    }
`;

const TextContainer = styled.div`
    padding-top: ${(props) => props.theme.spacing.sm};
    padding-inline-start: ${(props) => props.theme.spacing.sm};
    align-self: flex-start;
`;

const Title = styled.h3`
    font: ${(props) => props.theme.fonts.subtitle4};
    color: ${({ theme }) =>
        theme.custom?.story_title_font_color || theme.palette.common.white};
`;

const SStrapiImage = styled(StrapiImage)`
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    filter: brightness(90%);
`;

const StyledPlayButton = styled(PlayButton)`
    color: ${(props) => props.theme.custom?.story_cta_font_color};
    background: ${(props) => props.theme.custom?.story_cta_background_color};
    align-self: flex-end;
    margin: ${(props) => props.theme.spacing.sm};
`;

/* -------------------------------------------------------------------------- */
/*                                  COMPONENT                                 */
/* -------------------------------------------------------------------------- */

const StoryPreview = ({ story, context, id, storyLength }: PropsType) => {
    const router = useRouter();
    const { onTriggerEvent } = useTriggerEventContext();
    const trackFirstLast = () => {
        if (id === 0) {
            onTriggerEvent({
                event: Enum_Modals_Trigger.FirstStory,
                filters: { title: story.Title, slug: story.Slug },
            });
        } else if (id === storyLength - 1) {
            onTriggerEvent({
                event: Enum_Modals_Trigger.LastStory,
                filters: { title: story.Title, slug: story.Slug },
            });
        }
    };

    return (
        <InternalLink
            href={{
                pathname: '/[company]/[product]/story/[slug]',
                query: {
                    company: router.query['company'],
                    product: router.query['product'],
                    slug: story.Slug,
                    module: context.module,
                    story_order: context.story_order,
                    fromPage: context.fromPage,
                    searchQuery: context.searchQuery,
                },
            }}
            linkAs={`/${router.query['product']}/story/${story.Slug}`}
            onClick={trackFirstLast}
        >
            <Background
                className='story_card_background'
                id={story.Slug}
                url={getStrapiAssetUrl(story.PreviewImage)}
                // onClick={() => router.push(`/story/${story.Slug}`)}
            >
                <SStrapiImage
                    src={getStrapiAssetUrl(story.PreviewImage)!}
                    sizes='5rem'
                    formats={story.PreviewImage?.formats}
                    alt={story.PreviewImage?.alternativeText || story.Title}
                />
                <CardBody className='story_card_body'>
                    <TextContainer className='story_card_text'>
                        <Title className='story_title'>{story.Title}</Title>
                    </TextContainer>
                    <StyledPlayButton />
                </CardBody>
            </Background>
        </InternalLink>
    );
};

export default StoryPreview;
